import React from "react"

import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import {
  getMetricColorForMetric,
  resolveThreshold,
} from "@/metrics/metric-display"

import type { TestCaseRecord } from "@/types/api/TestCaseRecord"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import { AUDIO_REGION_COLOR_BY_CLASSIFICATION } from "../audio-metric-constants"
import { WithAudioGateMixin } from "../mixins/WithAudioGateMixin"
import { WithLLMAudioRegionsMixin } from "../mixins/WithLLMAudioRegionsMixin"
import { BinaryLLMMetricRecord } from "./BinaryLLMMetricRecord"

type BaseTestCaseRecord = Pick<
  TestCaseRecord,
  "input_str" | "expected_behaviors" | "expected_output_json"
>

export class AudioBinaryLLMMetricRecord extends WithLLMAudioRegionsMixin(
  WithAudioGateMixin(BinaryLLMMetricRecord),
) {
  documentation = this.metric_metadata?.question || this.description

  getOverviewValue(mo: RunMetricOutput[]): string | null {
    const valid = mo.filter((m) => m.float_value !== null && m.float_value >= 0)

    const average =
      valid.reduce(
        (a: number, b: RunMetricOutput) => a + (b.float_value || 0),
        0,
      ) / valid.length
    return (average * 100).toFixed(0)
  }

  _renderDeepDive(
    mo: RunMetricOutput,
    testCase: BaseTestCaseRecord,
  ): React.ReactNode {
    const audioSection = this._renderAudioObservationsSection(mo)
    if (!audioSection) {
      return super._renderDeepDive(mo, testCase)
    }
    return <div className="space-y-8">{audioSection}</div>
  }

  get supportsAudioSegmentReview(): boolean {
    return false
  }

  getAudioRegionColorForVerdict(verdict: string | null): string | undefined {
    const normalized = verdict?.toUpperCase()
    if (!normalized || normalized === ANNOTATION_VALUE.UNKNOWN) {
      return AUDIO_REGION_COLOR_BY_CLASSIFICATION.default
    }
    if (resolveThreshold(this)) {
      return AUDIO_REGION_COLOR_BY_CLASSIFICATION[
        getMetricColorForMetric(normalized, this)
      ]
    }
    // No success criteria: default to YES=success, NO=danger so polarity is visible.
    if (normalized === ANNOTATION_VALUE.YES) {
      return AUDIO_REGION_COLOR_BY_CLASSIFICATION.success
    }
    if (normalized === ANNOTATION_VALUE.NO) {
      return AUDIO_REGION_COLOR_BY_CLASSIFICATION.danger
    }
    return AUDIO_REGION_COLOR_BY_CLASSIFICATION.default
  }

  renderMetricType(): React.ReactNode {
    return "Binary"
  }
}
